import {
    Colors as ButtonColors,
    Variants as ButtonVariants,
} from "@jmc/solid-design-system/src/components/atoms/Button/Button";
import { Sizes } from "@jmc/solid-design-system/src/components/atoms/Grid/Grid";

import {
    FOUR_COLUMNS_2_2_4_4,
    FOUR_COLUMNS_2_4_2_4,
    FOUR_COLUMNS_2_4_4_2,
    FOUR_COLUMNS_4_2_2_4,
    FOUR_COLUMNS_4_2_4_2,
    FOUR_COLUMNS_4_4_2_2,
    ONE_COLUMN_12,
    SIX_COLUMNS_2_2_2_2_2_2,
    THREE_COLUMNS_2_4_6,
    THREE_COLUMNS_2_6_4,
    THREE_COLUMNS_4_2_6,
    THREE_COLUMNS_4_6_2,
    THREE_COLUMNS_6_2_4,
    THREE_COLUMNS_6_4_2,
    TWO_COLUMNS_3_9,
    TWO_COLUMNS_4_8,
    TWO_COLUMNS_8_4,
    TWO_COLUMNS_9_3,
} from "../../types/CMSRowProps";

export interface GridSizes {
    xs?: Sizes;
    sm?: Sizes;
    md?: Sizes;
    lg?: Sizes;
    xl?: Sizes;
}

export type CardColor =
    | "--text-primary" // TODO: Remove after rebranding
    | "--text-light" // TODO: Remove after rebranding
    | "--light" // TODO: Remove after rebranding
    | "--text-dark" // TODO: Remove after rebranding
    | "--dark" // TODO: Remove after rebranding
    | "--primary" // TODO: Remove after rebranding
    | "--primary-400" // TODO: Remove after rebranding
    | "--primary-300" // TODO: Remove after rebranding
    | "--primary-bg" // TODO: Remove after rebranding
    | "--secondary" // TODO: Remove after rebranding
    | "--secondary-400" // TODO: Remove after rebranding
    | "--secondary-300" // TODO: Remove after rebranding
    | "--secondary-bg" // TODO: Remove after rebranding
    | "--accent" // TODO: Remove after rebranding
    | "--accent-400" // TODO: Remove after rebranding
    | "--accent-300" // TODO: Remove after rebranding
    | "--accent-bg" // TODO: Remove after rebranding
    | "--grey" // TODO: Remove after rebranding
    | "--white"
    | "--transparent"
    | "--gray-100";

export type HeroColor = CardColor;
export type CarouselColor = CardColor;

const basicColorMap = new Map<string, CardColor>([
    ["White", "--white"],
    ["Primary", "--primary"],
    ["Primary Dark", "--primary-400"],
    ["Primary Light", "--primary-300"],
    ["Primary Background", "--primary-bg"],
    ["Secondary", "--secondary"],
    ["Secondary Dark", "--secondary-400"],
    ["Secondary Light", "--secondary-300"],
    ["Secondary Background", "--secondary-bg"],
    ["Accent", "--accent"],
    ["Accent Dark", "--accent-400"],
    ["Accent Light", "--accent-300"],
    ["Accent Background", "--accent-bg"],
    ["Gray", "--grey"],
]);

export const calloutColorMap = new Map<string, CardColor>([
    ["White", "white"],
    ["Primary", "primary"],
    ["Primary Dark", "primary"],
    ["Primary Light", "primary"],
    ["Primary Background", "primary"],
    ["Secondary", "primary"],
    ["Secondary Dark", "primary"],
    ["Secondary Light", "primary"],
    ["Secondary Background", "primary"],
    ["Accent", "white"],
    ["Accent Dark", "white"],
    ["Accent Light", "white"],
    ["Accent Background", "white"],
    ["Gray", "gray"],
]);

export const rebrandedCardColorMap = new Map<string, CardColor>([
    ["White", "--white"],
    ["Gray", "--gray-100"],
    ["Transparent", "--transparent"],
]);

export const cardColorMap = new Map(basicColorMap).set("Text Dark", "--text-dark").set("Text Light", "--text-light");
export const heroColorMap = new Map(basicColorMap).set("Text Dark", "--dark").set("Text Light", "--light");
export const carouselColorMap = cardColorMap;

type SectionColor = "default" | "text-dark" | "text-light" | "text-white";

export const sectionColorMap = new Map<string, SectionColor>([
    ["default", "--text-dark"],
    ["text-dark", "--text-dark"],
    ["text-light", "--text-light"],
    ["text-white", "--text-white"],
    ["primary", "--primary"],
    ["primary-dark", "--primary-400"],
    ["primary-light", "--primary-300"],
    ["primary-bg", "--primary-bg"],
    ["secondary", "--secondary"],
    ["secondary-dark", "--secondary-400"],
    ["secondary-light", "--secondary-300"],
    ["secondary-bg", "--secondary-bg"],
    ["accent", "--accent"],
    ["accent-dark", "--accent-400"],
    ["accent-light", "--accent-300"],
    ["accent-bg", "--accent-bg"],
    ["gray", "--grey"],
]);

export const variantMap = new Map<string, ButtonVariants>([
    ["Outlined", "outlined"],
    ["Contained", "contained"],
]);

export const colorMap = new Map<string, ButtonColors>([
    ["Secondary Color", "secondary"],
    ["Primary Color", "primary"],
    ["Accent Color", "accent"],
    ["Success Color", "success"],
    ["Warning Color", "warning"],
    ["Error Color", "error"],
    ["Grey Color", "grey"],
    ["Transparent Color", "transparent"],
]);

export const backgroundMap = new Map<string, string>([
    ["Primary", "primary"],
    ["Primary Light", "primary__light"],
    ["Primary Dark", "primary__dark"],
    ["Primary Background", "primary__background"],
    ["Secondary", "secondary"],
    ["Secondary Light", "secondary__light"],
    ["Secondary Dark", "secondary__dark"],
    ["Secondary Background", "secondary__background"],
    ["Accent", "accent"],
    ["Accent Light", "accent__light"],
    ["Accent Dark", "accent__dark"],
    ["Accent Background", "accent__background"],
    ["White", "white"],
    ["Gray", "gray"],
    // Why are we using both 'grey' and 'gray' throughout the codebase?
    // I chose 'grey' for separator based on seeing it elsewhere in the code,
    // but there's no 'grey' in this mapper, so I'm adding it now.
    ["Grey", "grey"],
]);

export const rebrandedBackgroundMap = new Map<string, string>([
    ["Primary", "gray"],
    ["Primary Light", "gray"],
    ["Primary Dark", "gray"],
    ["Primary Background", "gray"],
    ["Secondary", "gray"],
    ["Secondary Light", "gray"],
    ["Secondary Dark", "gray"],
    ["Secondary Background", "gray"],
    ["Accent", "gray"],
    ["Accent Light", "gray"],
    ["Accent Dark", "gray"],
    ["Accent Background", "gray"],
    ["White", "white"],
    ["Gray", "gray"],
    ["Grey", "gray"],
]);

export const gridMap = new Map<string, GridSizes>([
    [ONE_COLUMN_12, [{ xs: 12, sm: 12, md: 12 }]],
    [
        TWO_COLUMNS_4_8,
        [
            { xs: 12, sm: 6, md: 4, lg: 4 },
            { xs: 12, sm: 6, md: 8, lg: 8 },
        ],
    ],
    [
        TWO_COLUMNS_8_4,
        [
            { xs: 12, sm: 6, md: 8, lg: 8 },
            { xs: 12, sm: 6, md: 4, lg: 4 },
        ],
    ],
    [
        TWO_COLUMNS_3_9,
        [
            { xs: 12, sm: 6, md: 4, lg: 3 },
            { xs: 12, sm: 6, md: 8, lg: 9 },
        ],
    ],
    [
        TWO_COLUMNS_9_3,
        [
            { xs: 12, sm: 6, md: 8, lg: 9 },
            { xs: 12, sm: 6, md: 4, lg: 3 },
        ],
    ],
    [
        THREE_COLUMNS_2_4_6,
        [
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6 },
        ],
    ],
    [
        THREE_COLUMNS_2_6_4,
        [
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
        ],
    ],
    [
        THREE_COLUMNS_4_2_6,
        [
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6 },
        ],
    ],
    [
        THREE_COLUMNS_4_6_2,
        [
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
        ],
    ],
    [
        THREE_COLUMNS_6_2_4,
        [
            { xs: 12, sm: 6 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
        ],
    ],
    [
        THREE_COLUMNS_6_4_2,
        [
            { xs: 12, sm: 6 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
        ],
    ],
    [
        FOUR_COLUMNS_2_2_4_4,
        [
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
        ],
    ],
    [
        FOUR_COLUMNS_2_4_4_2,
        [
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
        ],
    ],
    [
        FOUR_COLUMNS_2_4_2_4,
        [
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
        ],
    ],
    [
        FOUR_COLUMNS_4_2_4_2,
        [
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
        ],
    ],
    [
        FOUR_COLUMNS_4_4_2_2,
        [
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
        ],
    ],
    [
        FOUR_COLUMNS_4_2_2_4,
        [
            { xs: 12, sm: 6, md: 3, lg: 4 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 2 },
            { xs: 12, sm: 6, md: 3, lg: 4 },
        ],
        [
            SIX_COLUMNS_2_2_2_2_2_2,
            [
                { xs: 12, sm: 6, md: 3, lg: 2 },
                { xs: 12, sm: 6, md: 3, lg: 2 },
                { xs: 12, sm: 6, md: 3, lg: 2 },
                { xs: 12, sm: 6, md: 3, lg: 2 },
                { xs: 12, sm: 6, md: 3, lg: 2 },
                { xs: 12, sm: 6, md: 3, lg: 2 },
            ],
        ],
    ],
]);

export const concertinaTypeMap = new Map<string, string>([
    ["None", "none"],
    ["First Open", "firstOpen"],
    ["Only One", "onlyOne"],
]);

export default {
    gridMap,
    cardColorMap,
    heroColorMap,
    calloutColorMap,
    sectionColorMap,
    carouselColorMap,
    variantMap,
    colorMap,
    backgroundMap,
    concertinaTypeMap,
    rebrandedBackgroundMap,
};
